import type { QueryClient, QueryKey } from '@tanstack/react-query'

interface ConnectQueryMeta {
  serviceName?: string
  methodName?: string
}

interface ConnectMethodRef {
  serviceName?: string
  methodName: string
}

function getConnectMeta(queryKey: QueryKey): ConnectQueryMeta | null {
  if (!Array.isArray(queryKey) || queryKey[0] !== 'connect-query') {
    return null
  }
  const meta = queryKey[1]
  if (!meta || typeof meta !== 'object') {
    return null
  }
  return meta as ConnectQueryMeta
}

export function invalidateConnectMethods(
  queryClient: QueryClient,
  methods: ConnectMethodRef[],
) {
  return queryClient.invalidateQueries({
    predicate: (query) => {
      const meta = getConnectMeta(query.queryKey)
      if (!meta?.methodName) return false
      return methods.some((method) =>
        method.methodName === meta.methodName &&
        (method.serviceName === undefined || method.serviceName === meta.serviceName))
    },
  })
}

export function invalidateAllConnectQueries(queryClient: QueryClient) {
  return queryClient.invalidateQueries({
    predicate: (query) => Array.isArray(query.queryKey) && query.queryKey[0] === 'connect-query',
  })
}

export function invalidateProjectList(queryClient: QueryClient) {
  return invalidateConnectMethods(queryClient, [
    { serviceName: 'catpocs.v1.ProjectService', methodName: 'ListProjects' },
  ])
}

export function invalidateProjectDetailReadModels(queryClient: QueryClient) {
  return invalidateConnectMethods(queryClient, [
    { serviceName: 'catpocs.v1.ProjectService', methodName: 'GetProject' },
    { serviceName: 'catpocs.v1.ProjectService', methodName: 'ListProjectMembers' },
    { serviceName: 'catpocs.v1.ProjectService', methodName: 'ListProjects' },
    { serviceName: 'catpocs.v1.ApplicationService', methodName: 'ListApplications' },
    { serviceName: 'catpocs.v1.ApplicationService', methodName: 'ListApplicationsWithIssues' },
  ])
}

export function invalidateApplicationReadModels(queryClient: QueryClient) {
  return invalidateConnectMethods(queryClient, [
    { serviceName: 'catpocs.v1.ApplicationService', methodName: 'GetApplication' },
    { serviceName: 'catpocs.v1.ApplicationService', methodName: 'ListApplications' },
    { serviceName: 'catpocs.v1.ApplicationService', methodName: 'ListApplicationsWithIssues' },
    { serviceName: 'catpocs.v1.ApplicationService', methodName: 'ListApplicationSetupAttempts' },
  ])
}

export function invalidateIssuesList(queryClient: QueryClient) {
  return invalidateConnectMethods(queryClient, [
    { serviceName: 'catpocs.v1.ApplicationService', methodName: 'ListApplicationsWithIssues' },
  ])
}

export function invalidateAdminUsersTables(queryClient: QueryClient) {
  return invalidateConnectMethods(queryClient, [
    { serviceName: 'catpocs.v1.AdminService', methodName: 'ListActiveSessions' },
    { serviceName: 'catpocs.v1.AdminService', methodName: 'ListUsers' },
    { serviceName: 'catpocs.v1.AdminService', methodName: 'GetUserStatistics' },
  ])
}

export function invalidateSettings(queryClient: QueryClient) {
  return invalidateConnectMethods(queryClient, [
    { serviceName: 'catpocs.v1.SettingsService', methodName: 'GetSettings' },
  ])
}

export function invalidateEcrAdminReadModels(queryClient: QueryClient) {
  return invalidateConnectMethods(queryClient, [
    { serviceName: 'catpocs.v1.ECRAdminService', methodName: 'GetRegistryOverview' },
    { serviceName: 'catpocs.v1.ECRAdminService', methodName: 'ListRepositories' },
    { serviceName: 'catpocs.v1.ECRAdminService', methodName: 'GetRepositoryDetails' },
    { serviceName: 'catpocs.v1.ECRAdminService', methodName: 'GetUnusedImages' },
    { serviceName: 'catpocs.v1.ECRAdminService', methodName: 'GetVulnerableImages' },
  ])
}

export function invalidateDeploymentReadModels(queryClient: QueryClient) {
  return invalidateConnectMethods(queryClient, [
    { serviceName: 'catpocs.v1.DeploymentService', methodName: 'ListImageTags' },
    { serviceName: 'catpocs.v1.DeploymentService', methodName: 'GetImageDeployments' },
    { serviceName: 'catpocs.v1.DeploymentService', methodName: 'ConfigureTagTracking' },
    { serviceName: 'catpocs.v1.ApplicationService', methodName: 'GetApplication' },
    { serviceName: 'catpocs.v1.ApplicationService', methodName: 'ListApplications' },
  ])
}
